import { useState, type CSSProperties, type ReactNode } from 'react';
import './charts.css';

/** Ein Stück eines Kreisdiagramms */
export interface Slice {
  key: string;
  label: string;
  value: number;
  /** Zusatz hinter dem Namen in der Legende, zum Beispiel die Zahl der Konten */
  note?: string;
  /** Farbe 1 bis 8 statt der Reihenfolge, zum Beispiel Rot für überfällig */
  color?: number;
}

/** Farbklasse des n-ten Stücks, die Farben stehen als Variablen in charts.css (hell und dunkel) */
export const colorClass = (index: number) => `chart-c${(index % 8) + 1}`;

const R = 42;
const C = 2 * Math.PI * R;

/**
 * Kreisdiagramm als Ring. Beim ersten Anzeigen zeichnet es sich in einem kurzen Schwung, bei geänderten Daten gleiten die Stücke in die neue Lage. Fährt man mit der Maus oder dem Fokus über ein Stück oder einen
 * Eintrag der Legende, hebt sich das Stück ab und die Mitte nennt Name, Betrag und Anteil. Die Legende steht als Liste daneben (`children`), das Diagramm selbst ist für Screenreader ein Bild mit Beschreibung.
 */
export function Donut({
  slices,
  active,
  onActive,
  title,
  total,
  format,
  ariaLabel,
}: {
  slices: Slice[];
  active: string | null;
  onActive: (key: string | null) => void;
  /** Beschriftung in der Mitte, solange kein Stück gewählt ist */
  title: string;
  total: string;
  format: (value: number) => string;
  ariaLabel: string;
}) {
  const sum = slices.reduce((s, x) => s + Math.max(0, x.value), 0);
  let start = 0;
  const arcs = slices.map((s, i) => {
    const part = sum > 0 ? Math.max(0, s.value) / sum : 0;
    const len = Math.max(0, part * C - (slices.length > 1 ? 1.2 : 0));
    const arc = { ...s, len, offset: -start * C, index: i, share: part };
    start += part;
    return arc;
  });
  const current = arcs.find((a) => a.key === active);
  /** Der Betrag in der Mitte wird so klein, dass er in den Ring passt: Der Innenkreis misst rund 58 % der Breite, eine Ziffer braucht etwa 0,58 Schriftgrößen. Die Einheit cqw bezieht sich auf die Breite des Rings. */
  const fit = (text: string) => `${Math.max(5.5, Math.min(12, 50 / (Math.max(text.length, 4) * 0.58)))}cqw`;

  return (
    <div className="donut" onMouseLeave={() => onActive(null)}>
      <svg viewBox="0 0 120 120" role="img" aria-label={ariaLabel} className="donut__svg">
        <circle className="donut__track" cx="60" cy="60" r={R} />
        <g transform="rotate(-90 60 60)">
          {arcs.filter((a) => a.len > 0).map((a) => (
            <circle
              key={a.key}
              className={`donut__seg ${colorClass(a.color ? a.color - 1 : a.index)}${active && active !== a.key ? ' is-dim' : ''}${active === a.key ? ' is-active' : ''}`}
              cx="60"
              cy="60"
              r={R}
              strokeDasharray={`${a.len} ${C - a.len}`}
              strokeDashoffset={a.offset}
              style={{ '--len': a.len, '--rest': C - a.len, '--c': C, '--delay': `${a.index * 45}ms` } as CSSProperties}
              onMouseEnter={() => onActive(a.key)}
            >
              <title>{`${a.label}: ${format(a.value)} (${Math.round(a.share * 100)} %)`}</title>
            </circle>
          ))}
        </g>
      </svg>
      <div className="donut__center" aria-hidden="true">
        {current ? (
          <>
            <span className="donut__name">{current.label}</span>
            <strong style={{ fontSize: fit(format(current.value)) }}>{format(current.value)}</strong>
            <span className="donut__share">{Math.round(current.share * 100)} %</span>
          </>
        ) : (
          <>
            <span className="donut__name">{title}</span>
            <strong style={{ fontSize: fit(total) }}>{total}</strong>
          </>
        )}
      </div>
    </div>
  );
}

/** Legende eines Kreisdiagramms: ein Eintrag je Stück, bedienbar mit Maus und Tastatur */
export function Legend({ slices, active, onActive, format }: { slices: Slice[]; active: string | null; onActive: (key: string | null) => void; format: (value: number) => string }) {
  const sum = slices.reduce((s, x) => s + Math.max(0, x.value), 0);
  return (
    <ul className="legend">
      {slices.map((s, i) => (
        <li key={s.key} className={active === s.key ? 'is-active' : active ? 'is-dim' : ''}>
          <button type="button" onMouseEnter={() => onActive(s.key)} onFocus={() => onActive(s.key)} onBlur={() => onActive(null)} onMouseLeave={() => onActive(null)}>
            <span className={`legend__dot ${colorClass(s.color ? s.color - 1 : i)}`} aria-hidden="true" />
            <span className="legend__label">{s.label}</span>
            <span className="legend__value">{format(s.value)}</span>
            <span className="legend__share">{sum > 0 ? Math.round((Math.max(0, s.value) / sum) * 100) : 0} %</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

/** Waagerechter Balken, der beim Anzeigen von links wächst. `value` und `max` bestimmen die Länge. */
export function Bar({ value, max, color = 1, label }: { value: number; max: number; color?: number; label?: ReactNode }) {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <div className="bar" role="presentation">
      <span className={`bar__fill ${colorClass(color - 1)}`} style={{ width: `${pct}%` }} />
      {label}
    </div>
  );
}

/** Gleichmäßige Achsenwerte, die den Bereich von 0 bis `hi` überdecken. Bei Zählwerten (`integer`) nie feiner als 1. */
function axisTicks(hi: number, integer: boolean): number[] {
  const raw = Math.max(hi, 1) / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const norm = raw / mag;
  let step = (norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10) * mag;
  if (integer) step = Math.max(1, Math.ceil(step));
  const ticks: number[] = [];
  for (let v = 0; v <= Math.ceil(Math.max(hi, 1) / step) * step + step / 1e6; v += step) ticks.push(Math.round(v * 100) / 100);
  return ticks;
}

export interface ColumnSeries {
  key: string;
  label: string;
  /** Farbe 1 bis 8 */
  color: number;
  values: number[];
  /** Beim ersten Anzeigen ausgeblendet, der Benutzer schaltet sie zu */
  off?: boolean;
}

/**
 * Säulendiagramm mit mehreren Reihen je Rubrik (zum Beispiel Monat). Die Einträge oben blenden Reihen ein und aus, die Achse passt sich an, die Säulen wachsen beim ersten Anzeigen von unten.
 * Mit Maus oder Tastatur auf einer Rubrik erscheinen alle Werte. Für Screenreader trägt jede Rubrik die Werte als Beschriftung.
 */
export function ColumnChart({ labels, series, format = (v) => v.toLocaleString('de-DE'), integer = true, ariaLabel }: { labels: string[]; series: ColumnSeries[]; format?: (value: number) => string; integer?: boolean; ariaLabel: string }) {
  const [hidden, setHidden] = useState<string[]>(() => series.filter((s) => s.off).map((s) => s.key));
  const [hover, setHover] = useState<number | null>(null);
  const shown = series.filter((s) => !hidden.includes(s.key));
  const hi = Math.max(0, ...shown.flatMap((s) => s.values));
  const ticks = axisTicks(hi, integer);
  const top = ticks[ticks.length - 1]!;
  const pct = (v: number) => (top > 0 ? (v / top) * 100 : 0);
  const toggle = (key: string) => setHidden((h) => (h.includes(key) ? h.filter((x) => x !== key) : [...h, key]));
  const side = hover === null ? '' : hover < 2 ? ' colchart__tip--left' : hover > labels.length - 3 ? ' colchart__tip--right' : '';

  return (
    <div className="colchart">
      <ul className="colchart__legend">
        {series.map((s) => (
          <li key={s.key}>
            <button type="button" className="colchart__toggle" aria-pressed={!hidden.includes(s.key)} onClick={() => toggle(s.key)}>
              <span className={`legend__dot ${colorClass(s.color - 1)}`} aria-hidden="true" /> {s.label}
            </button>
          </li>
        ))}
      </ul>
      <div className="colchart__plot" onMouseLeave={() => setHover(null)}>
        {ticks.map((t) => (
          <div key={t} className="colchart__grid" style={{ bottom: `${pct(t)}%` }}><span>{t.toLocaleString('de-DE')}</span></div>
        ))}
        <div className="colchart__cols" role="group" aria-label={ariaLabel} style={{ gridTemplateColumns: `repeat(${labels.length}, minmax(0, 1fr))` }}>
          {labels.map((label, i) => (
            <div
              key={label}
              className={`colchart__col${hover === i ? ' is-hover' : ''}`}
              tabIndex={0}
              aria-label={`${label}: ${series.map((s) => `${s.label} ${format(s.values[i] ?? 0)}`).join(', ')}`}
              onMouseEnter={() => setHover(i)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
            >
              {shown.map((s, k) => (
                <span key={s.key} className={`colchart__bar ${colorClass(s.color - 1)}`} style={{ height: `${pct(s.values[i] ?? 0)}%`, animationDelay: `${i * 25 + k * 40}ms` }} />
              ))}
            </div>
          ))}
        </div>
        {hover !== null && (
          <div className={`colchart__tip${side}`} style={{ left: `${((hover + 0.5) / labels.length) * 100}%` }} aria-hidden="true">
            <strong>{labels[hover]}</strong>
            {series.filter((s) => !hidden.includes(s.key)).map((s) => (
              <span key={s.key}><i className={`legend__dot ${colorClass(s.color - 1)}`} /> {s.label} <b>{format(s.values[hover] ?? 0)}</b></span>
            ))}
          </div>
        )}
      </div>
      <div className="colchart__labels" aria-hidden="true" style={{ gridTemplateColumns: `repeat(${labels.length}, minmax(0, 1fr))` }}>{labels.map((l) => <span key={l}>{l}</span>)}</div>
    </div>
  );
}
